// What the patient's booking page shows, and when it may book.
//
// The page is opened once, on a phone, by somebody who may be eighty. So it
// has to say how many things there are to do, which one is left, and offer
// exactly one button. A link usually holds times for one appointment; a
// wellness exam is two — a lab draw and then the doctor — and both are
// booked together or not at all.
//
// The server decides what a link may book (linkRules.validatePicks). This
// mirrors it so the button is never offered for something the server would
// refuse.
import { patientDayLabel, patientTimeLabel } from './publicBookingTime';

export interface PlanSlot { date: string; time: string; }

export interface PlanAppointment {
  /** What the patient sees and sends back. No id leaves the server. */
  step: number;
  serviceName: string;
  providerName: string;
  durationMinutes: number;
  slots: PlanSlot[];
}

export type Picks = Record<number, PlanSlot | undefined>;

export interface BookingPlan {
  intro: string;
  ready: boolean;
  /** Steps that still need a time. */
  missing: number[];
  /** Why the button is not offered, in the patient's words; null when it is. */
  blocker: string | null;
  /** One line per appointment that has a time. */
  summary: string[];
  button: string;
  /** What to send. Complete only when `ready`. */
  picks: Array<{ step: number; date: string; time: string }>;
}

/** An appointment with one time has nothing to choose: it starts chosen. */
export function initialPicks(appointments: PlanAppointment[]): Picks {
  const picks: Picks = {};
  appointments.forEach((a) => { if (a.slots.length === 1) picks[a.step] = a.slots[0]; });
  return picks;
}

const what = (a: PlanAppointment): string =>
  `${a.serviceName || 'Appointment'}${a.providerName ? ` with ${a.providerName}` : ''}`;

const minutesOf = (s: PlanSlot): number => {
  const [y, m, d] = s.date.split('-').map(Number);
  const [hh, mm] = s.time.split(':').map(Number);
  return Date.UTC(y, m - 1, d, hh, mm) / 60000;
};

const list = (steps: number[]): string =>
  steps.length === 1 ? `appointment ${steps[0]}` : `appointments ${steps.slice(0, -1).join(', ')} and ${steps[steps.length - 1]}`;

export function bookingPlan(appointments: PlanAppointment[], picks: Picks): BookingPlan {
  const many = appointments.length > 1;
  const nothingToChoose = appointments.length > 0 && appointments.every((a) => a.slots.length === 1);
  const missing = appointments.filter((a) => !picks[a.step]).map((a) => a.step);
  const chosen = appointments.filter((a) => picks[a.step]).map((a) => ({ a, slot: picks[a.step] as PlanSlot }));

  const intro = many
    ? (nothingToChoose
      ? `We are holding ${appointments.length} appointments for you. Confirm them below and they are yours.`
      : `You have ${appointments.length} appointments to book. Choose a time for each, then book them together.`)
    : (nothingToChoose
      ? 'We are holding this time for you. Confirm it below and it is yours.'
      : 'Tap the time that suits you. It is yours as soon as you confirm.');

  // Nobody is in two rooms at once. Back to back is fine.
  let overlap = false;
  for (let i = 0; i < chosen.length; i += 1) {
    for (let j = i + 1; j < chosen.length; j += 1) {
      const aStart = minutesOf(chosen[i].slot), aEnd = aStart + (chosen[i].a.durationMinutes || 0);
      const bStart = minutesOf(chosen[j].slot), bEnd = bStart + (chosen[j].a.durationMinutes || 0);
      if (aStart < bEnd && bStart < aEnd) overlap = true;
    }
  }

  const blocker = missing.length
    ? (many ? `Choose a time for ${list(missing)}.` : 'Choose a time above.')
    : (overlap ? 'Those two appointments overlap. Please choose different times.' : null);

  return {
    intro,
    ready: blocker === null && appointments.length > 0,
    missing,
    blocker,
    summary: chosen.map(({ a, slot }) => `${what(a)} — ${patientDayLabel(slot.date)} at ${patientTimeLabel(slot.time)}`),
    button: !many ? 'Book this time' : (appointments.length === 2 ? 'Book both appointments' : `Book all ${appointments.length} appointments`),
    picks: chosen.map(({ a, slot }) => ({ step: a.step, date: slot.date, time: slot.time })),
  };
}

// ---- How the patient reaches the practice -----------------------------------
//
// The page said "call the practice" and gave no number. The practice sets one
// in Admin (practice_phone); with none set the page does not point at
// something it cannot show. Returned in three parts so the page can make the
// number a tappable link.

export interface Advice { before: string; phone: string | null; after: string; }

const clean = (phone?: string | null): string | null => String(phone || '').trim() || null;

/** tel: link for a number however it was typed; ten digits are taken as US. */
export function telHref(phone: string): string {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  return `tel:${digits}`;
}

/** Under "This link is no longer available". */
export function goneAdvice(phone?: string | null): Advice {
  const number = clean(phone);
  const lead = 'It may already have been used, or it may have expired. Please reply to our text';
  return number
    ? { before: `${lead} or call us at `, phone: number, after: ' and we will send you a new one.' }
    : { before: `${lead} and we will send you a new one.`, phone: null, after: '' };
}

/**
 * Under "You are booked for…" (confirmed) or "We have you down for…" (a
 * request). `texted` is whether the confirmation text really went; the page
 * says so only then, so a quiet phone is never blamed on the page. A request
 * is never texted — the office answers it — so the flag is ignored for one.
 */
export function changeAdvice(confirmed: boolean, phone?: string | null, texted = false): Advice {
  const number = clean(phone);
  const lead = confirmed
    ? `${texted ? 'We have texted you this confirmation as well. ' : ''}You do not need to do anything else. If anything changes, just reply to our text`
    : 'The practice will confirm this with you shortly. If anything changes in the meantime, just reply to our text';
  return number
    ? { before: `${lead} or call us at `, phone: number, after: '.' }
    : { before: `${lead}.`, phone: null, after: '' };
}
